import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TextField } from "@mui/material";

import { roundAndPadMoneyValue } from "services/utility/formatting";
import { checkForUnexpectedProps } from "services/utility/misc";

export function FrmTextField(props) {
  const {
    value: initialValue,
    multiline,
    number,
    money,
    moneyDecimalPlaces,
    maxLength,
    children,
    readOnly,
    onValueChange,
    onValueBlur,
    dataTesting,
    disabled,
    autoFocus,
    label,
    error,
    name,
    placeholder,
    helperText,
    autoComplete = "off",
    type,
    select,
    id,
    startAdornment,
    endAdornment,
    minWidth,
    sx,
    required,
    detectAutofill = false,
    ...unexpected
  } = props;
  checkForUnexpectedProps("FrmTextField", unexpected);

  const [value, setValue] = useState("");
  const [updated, setUpdated] = useState(false);
  const inputRef = useRef(null);

  // Detect autofill using animation trick (only when enabled)
  useEffect(() => {
    if (!detectAutofill) {
      return;
    }

    const input = inputRef.current?.querySelector("input");
    if (!input) {
      return;
    }

    const handleAutofill = (e) => {
      if (e.animationName === "onAutoFillStart") {
        const autofillValue = e.target.value;
        setValue(autofillValue);
        onValueBlur?.(name, autofillValue);
      }
    };

    input.addEventListener("animationstart", handleAutofill);
    return () => input.removeEventListener("animationstart", handleAutofill);
  }, [detectAutofill, name, onValueBlur]);

  const currencyMinimumFractionDigits = useSelector(
    (state) =>
      state.userCompanySettings.localization.currencyMinimumFractionDigits
  );

  useEffect(() => {
    if (money) {
      setValue(
        roundAndPadMoneyValue(
          initialValue,
          currencyMinimumFractionDigits,
          moneyDecimalPlaces
            ? moneyDecimalPlaces
            : currencyMinimumFractionDigits,
          false
        )
      );
    } else {
      setValue(initialValue);
    }
  }, [initialValue, money, moneyDecimalPlaces, currencyMinimumFractionDigits]);

  let inputSx = { textAlign: number || money ? "right" : "left" };
  if (!multiline) {
    inputSx = {
      ...inputSx,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    };
  }

  function onChange(e) {
    const { name, value } = e.target;
    if (maxLength && value.length > maxLength) {
      return;
    }
    setUpdated(true);
    setValue(value);
    onValueChange && onValueChange(name, value);
  }

  function handleValueBlur(e) {
    if (updated && onValueBlur) {
      onValueBlur(e.target.name, e.target.value);
      setUpdated(false);
    }
  }

  return (
    <TextField
      ref={inputRef}
      label={label}
      id={id}
      name={name}
      disabled={disabled}
      autoComplete={autoComplete}
      margin="dense"
      onChange={onChange}
      onBlur={handleValueBlur}
      value={value || value === 0 ? value : ""}
      multiline={multiline}
      variant="outlined"
      fullWidth
      inputProps={{ readOnly, sx: inputSx }}
      InputProps={{ startAdornment, endAdornment }}
      SelectProps={{
        IconComponent: KeyboardArrowDownIcon,
        MenuProps: { disableScrollLock: true },
      }}
      sx={
        detectAutofill
          ? {
              ...sx,
              "& input:-webkit-autofill": { animation: "onAutoFillStart 0s" },
              "@keyframes onAutoFillStart": {},
            }
          : sx
      }
      InputLabelProps={{ shrink: true }}
      data-testing={dataTesting}
      autoFocus={autoFocus}
      error={error}
      placeholder={placeholder}
      helperText={helperText}
      type={money || number ? "number" : type}
      select={select}
      required={required}
    >
      {children}
    </TextField>
  );
}
